import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './InterviewSchedule.css';
import API_BASE_URL from '../config';
import { toast } from "react-toastify";
import {
  FaListUl,
  FaCode,
  FaKeyboard,
  FaSitemap,
  FaBug,
  FaDatabase,
  FaBullseye,
  FaBookOpen,
  FaQuestionCircle,
  FaVideo,
  FaPhone,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaClock,
  FaUsers,
  FaStickyNote,
  FaMagic,
  FaTrash,
  FaBriefcase,
  FaEnvelope
} from "react-icons/fa";

// Jitsi Video Call Component (Only for non-assessment interviews)
const JitsiVideoCall = ({ roomName, userName, userEmail, onClose, isHost = true }) => {
  const jitsiContainerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [api, setApi] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadJitsiScript = () => {
      return new Promise((resolve, reject) => {
        if (window.JitsiMeetExternalAPI) {
          resolve();
          return;
        }
        
        const script = document.createElement('script');
        script.src = 'https://meet.jit.si/external_api.js';
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Jitsi script'));
        document.head.appendChild(script);
      });
    };

    const initializeJitsi = async () => {
      try {
        await loadJitsiScript();
        
        if (!isMounted) return;

        const domain = 'meet.jit.si';
        const options = {
          roomName: roomName,
          parentNode: jitsiContainerRef.current,
          userInfo: {
            displayName: userName,
            email: userEmail
          },
          configOverwrite: {
            startWithVideoMuted: false,
            startWithAudioMuted: false,
            disableDeepLinking: true,
            disableInviteFunctions: false,
            prejoinPageEnabled: false,
            enableClosePage: true,
            disableProfile: false,
            enableCalendarIntegration: false,
            requireDisplayName: true,
            toolbarButtons: [
              'microphone', 'camera', 'chat', 'share-screen', 
              'raise-hand', 'video-quality', 'fullscreen', 
              'settings', 'hangup'
            ]
          },
          interfaceConfigOverwrite: {
            SHOW_JITSI_WATERMARK: false,
            SHOW_BRAND_WATERMARK: false,
            TOOLBAR_BUTTONS: [
              'microphone', 'camera', 'chat', 'share-screen',
              'raise-hand', 'video-quality', 'fullscreen',
              'settings', 'hangup'
            ]
          }
        };

        const jitsiApi = new window.JitsiMeetExternalAPI(domain, options);
        
        jitsiApi.addListener('readyToClose', () => {
          if (onClose) onClose();
        });

        jitsiApi.addListener('videoConferenceJoined', () => {
          setIsLoading(false);
        });

        setApi(jitsiApi);
      } catch (err) {
        console.error('Error initializing Jitsi:', err);
        setError(err.message);
        setIsLoading(false);
      }
    };

    initializeJitsi();

    return () => {
      isMounted = false;
      if (api) {
        api.dispose();
      }
    };
  }, [roomName, userName, userEmail, onClose]);

  if (error) {
    return (
      <div className="jitsi-error-container">
        <div className="jitsi-error">
          <h4>⚠️ Video Call Error</h4>
          <p>{error}</p>
          <button onClick={onClose} className="close-video-btn">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="jitsi-container">
      {isLoading && (
        <div className="jitsi-loading">
          <div className="loading-spinner"></div>
          <p>Starting video call...</p>
        </div>
      )}
      <div ref={jitsiContainerRef} style={{ width: '100%', height: '100%', minHeight: '500px' }} />
    </div>
  );
};

const InterviewSchedule = ({ application, onClose, onSchedule }) => {
  const [interviewData, setInterviewData] = useState({
    scheduledDate: '',
    scheduledTime: '',
    interviewType: 'Technical',
    interviewMode: 'Video Call',
    interviewers: '',
    meetingLink: '',
    assessmentLink: '',
    location: '',
    notes: '',
    duration: '60',
    selectedQuestions: [],
    autoGenerateAssessment: true,
    useJitsiMeet: false
  });

  const [errors, setErrors] = useState({});
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [showQuestionSelection, setShowQuestionSelection] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [generatingAssessment, setGeneratingAssessment] = useState(false);
  const [assessmentCreated, setAssessmentCreated] = useState(false);
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [jitsiRoomName, setJitsiRoomName] = useState('');

  // Get current user
  useEffect(() => {
    const getUserFromSession = () => {
      try {
        const userDataString = sessionStorage.getItem('userData');
        if (userDataString) {
          const userData = JSON.parse(userDataString);
          setCurrentUser({
            email: userData.email,
            name: userData.name || userData.userName || 'User',
            userId: userData._id || userData.userId
          });
        }
      } catch (error) {
        console.error('Error getting user from session:', error);
      }
    };
    getUserFromSession();
  }, []);

  // Load questions ONLY when Assessment is selected
  useEffect(() => {
    if (interviewData.interviewType === 'Assessment' && currentUser && application?.appliedRole) {
      loadQuestions();
    } else {
      setQuestions([]);
      setInterviewData(prev => ({
        ...prev,
        selectedQuestions: [],
        autoGenerateAssessment: true
      }));
    }
  }, [interviewData.interviewType, currentUser, application?.appliedRole]);

  // Load questions from question bank based on job position
  const loadQuestions = async () => {
    try {
      if (!currentUser) return;

      setLoadingQuestions(true);
      const token = sessionStorage.getItem('authToken');
      
      // ✅ IMPORTANT: Filter questions by job position
      const response = await axios.get(`${API_BASE_URL}/api/questions`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        params: { 
          userEmail: currentUser.email,
          jobPosition: application.appliedRole // Only get questions for this position
        }
      });

      if (response.data.success) {
        // Filter questions for the specific job position
        const filteredQuestions = response.data.data.filter(q => 
          q.jobPosition === application.appliedRole || !q.jobPosition
        );
        setQuestions(filteredQuestions);
        console.log(`📝 Loaded ${filteredQuestions.length} questions for ${application.appliedRole}`);
        
        if (filteredQuestions.length === 0) {
          toast.info(`No questions available for ${application.appliedRole}. Please add questions to the question bank.`);
        }
      }
    } catch (error) {
      console.error('Error loading questions:', error);
      toast.error('Failed to load questions');
    } finally {
      setLoadingQuestions(false);
    }
  };

const generateJitsiRoom = () => {
  const roomId = `interview-${application._id}-${Date.now()}`;
  const roomUrl = `https://meet.jit.si/${roomId}`;
  setJitsiRoomName(roomId);
  return roomUrl; // ✅ மட்டும்
};

  // Start video call (only for non-assessment)
const startVideoCall = () => {

  if (interviewData.interviewType === 'Assessment') {
    toast.error('Assessmentக்கு video call இல்லை');
    return;
  }

  if (interviewData.useJitsiMeet) {
    // 👉 Jitsi open
    setShowVideoCall(true);
  } else {
    // 👉 External link open (Google Meet / Zoom)
    if (interviewData.meetingLink) {
      window.open(interviewData.meetingLink, "_blank");
    } else {
      toast.error("Meeting link இல்லை");
    }
  }
};

  // Generate assessment link (ONLY for Assessment type)
  const generateAssessmentLink = async () => {
    if (!currentUser || interviewData.selectedQuestions.length === 0) {
      toast.error('Please select at least one question first');
      return null;
    }

    try {
      setGeneratingAssessment(true);
      const token = sessionStorage.getItem('authToken');
      
      const assessmentResponse = await axios.post(
        `${API_BASE_URL}/api/assessments`,
        {
          title: `${application.name} - ${application.appliedRole} Assessment`,
          description: `Technical assessment for ${application.name} - ${application.appliedRole} position`,
          duration: parseInt(interviewData.duration),
          candidateName: application.name,
          candidateEmail: application.email,
          applicationId: application._id,
          position: application.appliedRole,
          userEmail: currentUser.email,
          questions: interviewData.selectedQuestions,
          autoGenerate: true
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (assessmentResponse.data.success) {
        const assessment = assessmentResponse.data.data;
        
        const assessmentUrl = `${window.location.origin}/assessment/${assessment.assessmentId}?candidateEmail=${encodeURIComponent(application.email)}&applicationId=${application._id}&candidateName=${encodeURIComponent(application.name)}`;
        
        setAssessmentCreated(true);
        toast.success('Assessment created successfully!');
        
        setInterviewData(prev => ({
          ...prev,
          meetingLink: assessmentUrl,
          assessmentLink: assessmentUrl
        }));
        
        return assessmentUrl;
      }
    } catch (error) {
      console.error('❌ Error creating assessment:', error);
      toast.error(error.response?.data?.message || 'Failed to create assessment');
      return null;
    } finally {
      setGeneratingAssessment(false);
    }
  };

const handleChange = (field, value) => {
  setInterviewData(prev => ({
    ...prev,
    [field]: value
  }));
  
  if (errors[field]) {
    setErrors(prev => ({ ...prev, [field]: '' }));
  }

  // ✅ When switching to AI Interview, set appropriate mode
  if (field === 'interviewType') {
    if (value === 'Assessment') {
      setInterviewData(prev => ({
        ...prev,
        interviewMode: 'Online Test',
        meetingLink: '',
        useJitsiMeet: false,
        selectedQuestions: []
      }));
    } 
    // ✅ NEW: AI Interview mode
    else if (value === 'AI Interview') {
      setInterviewData(prev => ({
        ...prev,
        interviewMode: 'AI Interview',
        meetingLink: '',
        useJitsiMeet: false
      }));
    }
    else {
      setInterviewData(prev => ({
        ...prev,
        interviewMode: 'Video Call',
        autoGenerateAssessment: false
      }));
    }
  }
};

  const validateForm = () => {
    const newErrors = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!interviewData.scheduledDate) {
      newErrors.scheduledDate = 'Interview date is required';
    } else {
      const selectedDate = new Date(interviewData.scheduledDate);
      selectedDate.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        newErrors.scheduledDate = 'Interview date must be in the future';
      }
    }

    if (!interviewData.scheduledTime) {
      newErrors.scheduledTime = 'Interview time is required';
    } else {
      const selectedDateTime = new Date(`${interviewData.scheduledDate}T${interviewData.scheduledTime}`);
      const now = new Date();
      
      if (selectedDateTime <= now) {
        newErrors.scheduledTime = 'Interview time must be in the future';
      }
    }

    // ✅ Assessment specific validation
    if (interviewData.interviewType === 'Assessment') {
      if (interviewData.selectedQuestions.length === 0) {
        newErrors.selectedQuestions = 'Please select at least one question for assessment';
      }
    } else {
      // Non-assessment validation
      if (interviewData.interviewMode === 'Video Call' && !interviewData.meetingLink && !interviewData.useJitsiMeet) {
        newErrors.meetingLink = 'Meeting link is required for video call';
      }

      if (interviewData.interviewMode === 'In-Person' && !interviewData.location) {
        newErrors.location = 'Location is required for in-person interview';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      let finalMeetingLink = interviewData.meetingLink;
      
      // ✅ For Assessment: Generate assessment link if not created
      if (interviewData.interviewType === 'Assessment') {
        if (interviewData.selectedQuestions.length === 0) {
          toast.error('Please select questions for the assessment');
          return;
        }
        
        if (!assessmentCreated) {
          const assessmentUrl = await generateAssessmentLink();
          if (assessmentUrl) {
            finalMeetingLink = assessmentUrl;
          } else {
            toast.error('Failed to generate assessment link');
            return;
          }
        }
      } 
      // ✅ For Video Call: Generate Jitsi meeting if using built-in
    else if (interviewData.interviewMode === 'Video Call') {

if (!interviewData.useJitsiMeet) {
  // 🔥 External link இருந்தா Jitsi override ஆகக்கூடாது
  finalMeetingLink = interviewData.meetingLink;
}
 else {
    // 👉 External link selected
    if (!finalMeetingLink) {
      toast.error("Please enter external meeting link");
      return;
    }
  }
}


if (interviewData.interviewType === 'AI Interview') {
  finalMeetingLink = `${window.location.origin}/ai-interview/${application._id}`;
}

      const scheduledDateTime = `${interviewData.scheduledDate}T${interviewData.scheduledTime}`;
      
      const interviewPayload = {
        scheduledDate: scheduledDateTime,
        interviewType: interviewData.interviewType,
        interviewMode: interviewData.interviewType === 'Assessment' ? 'Online Test' : interviewData.interviewMode,
        interviewers: interviewData.interviewers.split(',').map(i => i.trim()).filter(i => i),
        meetingLink: finalMeetingLink,
        location: interviewData.location,
        notes: interviewData.notes,
        duration: parseInt(interviewData.duration),
        assessmentLink: finalMeetingLink,
        selectedQuestions: interviewData.selectedQuestions,
        isAssessment: interviewData.interviewType === 'Assessment',
        jitsiRoomName: jitsiRoomName
      };

      onSchedule(application._id, interviewPayload);
      toast.success(interviewData.interviewType === 'Assessment' ? 'Assessment scheduled successfully!' : 'Interview scheduled successfully!');
      
    } catch (error) {
      console.error('Error submitting interview:', error);
      toast.error('Failed to schedule interview');
    }
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const toggleQuestionSelection = (questionId) => {
    const isSelected = interviewData.selectedQuestions.includes(questionId);
    const updatedQuestions = isSelected
      ? interviewData.selectedQuestions.filter(id => id !== questionId)
      : [...interviewData.selectedQuestions, questionId];
    
    setInterviewData(prev => ({
      ...prev,
      selectedQuestions: updatedQuestions
    }));

    // Reset assessment created flag when questions change
    if (assessmentCreated) {
      setAssessmentCreated(false);
    }
  };

  const getQuestionTypeIcon = (type) => {
    const typeIcons = {
      multiple_choice: <FaListUl />,
      coding: <FaCode />,
      text: <FaKeyboard />,
      system_design: <FaSitemap />,
      debugging: <FaBug />,
      sql: <FaDatabase />,
      scenario: <FaBullseye />,
      case_study: <FaBookOpen />
    };
    return typeIcons[type] || <FaQuestionCircle />;
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty.toLowerCase()) {
      case 'easy': return '#10b981';
      case 'medium': return '#f59e0b';
      case 'hard': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const handleAutoGenerateAssessment = async () => {
    if (interviewData.selectedQuestions.length === 0) {
      toast.error('Please select questions first');
      return;
    }
    const assessmentUrl = await generateAssessmentLink();
    if (assessmentUrl) {
      toast.success('Assessment link generated!');
    }
  };

  return (
    <>
      {/* Video Call Modal - Only shown for non-assessment interviews */}
      {showVideoCall && interviewData.interviewType !== 'Assessment' && (
        <div className="video-call-modal-overlay">
          <div className="video-call-modal">
            <div className="video-call-header">
              <h3>
                <FaVideo /> Interview with {application.name}
              </h3>
              <button onClick={() => setShowVideoCall(false)} className="close-video-btn">
                ×
              </button>
            </div>
            <JitsiVideoCall
              roomName={jitsiRoomName || `interview-${application._id}`}
              userName={currentUser?.name || 'Interviewer'}
              userEmail={currentUser?.email || ''}
              onClose={() => setShowVideoCall(false)}
            />
          </div>
        </div>
      )}

      {/* Schedule Interview Modal */}
      <div className="interview-modal-overlay">
        <div className="interview-modal">
          <div className="interview-modal-header">
            <div className="interview-modal-title">
              <svg className="interview-modal-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <div>
                <h2>{interviewData.interviewType === 'Assessment' ? 'Schedule Assessment' : 'Schedule Interview'}</h2>
                <p className="interview-modal-subtitle">
                  {interviewData.interviewType === 'Assessment' 
                    ? 'Create and schedule an online assessment for the candidate' 
                    : 'Plan and schedule candidate interview'}
                </p>
              </div>
            </div>
            <button className="interview-modal-close" onClick={onClose}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="interview-candidate-info">
            <div className="candidate-header">
              <div className="candidate-avatar">
                {application.name?.charAt(0).toUpperCase() || '?'}
              </div>
              <div className="candidate-details">
                <h3>{application.name}</h3>
                <div className="candidate-meta">
                  <span className="candidate-meta-item">
                    <FaBriefcase /> {application.appliedRole}
                  </span>
                  <span className="candidate-meta-item">
                    <FaEnvelope /> {application.email}
                  </span>
                  <span className="candidate-meta-item">
                    <FaPhone /> {application.phone}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="interview-form">
            {/* Date & Time Section */}
            <div className="form-section">
              <div className="form-section-header">
                <h4><FaCalendarAlt /> Date & Time</h4>
                <p className="form-section-subtitle">Select future date and time for the {interviewData.interviewType === 'Assessment' ? 'assessment' : 'interview'}</p>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>{interviewData.interviewType === 'Assessment' ? 'Assessment Date' : 'Interview Date'} *</label>
                  <div className="form-input-wrapper">
                    <input
                      type="date"
                      value={interviewData.scheduledDate}
                      onChange={(e) => handleChange('scheduledDate', e.target.value)}
                      min={getMinDate()}
                      className={errors.scheduledDate ? 'input-error' : ''}
                    />
                    {errors.scheduledDate && <div className="form-error">{errors.scheduledDate}</div>}
                  </div>
                </div>

                <div className="form-group">
                  <label>{interviewData.interviewType === 'Assessment' ? 'Assessment Time' : 'Interview Time'} *</label>
                  <div className="form-input-wrapper">
                    <input
                      type="time"
                      value={interviewData.scheduledTime}
                      onChange={(e) => handleChange('scheduledTime', e.target.value)}
                      className={errors.scheduledTime ? 'input-error' : ''}
                    />
                    {errors.scheduledTime && <div className="form-error">{errors.scheduledTime}</div>}
                  </div>
                </div>
              </div>
            </div>

            {/* Interview Details Section */}
            <div className="form-section">
              <div className="form-section-header">
                <h4>Details</h4>
                <p className="form-section-subtitle">Configure type and mode</p>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={interviewData.interviewType}
                    onChange={(e) => handleChange('interviewType', e.target.value)}
                  >
                    <option value="Technical">Technical Interview</option>
                    <option value="HR">HR Interview</option>
                    <option value="Managerial">Managerial Round</option>
                    <option value="Assessment">Assessment Round</option>
                    <option value="Cultural Fit">Cultural Fit</option>
                    <option value="Final">Final Round</option>
                    <option value="AI Interview">🤖 AI Interview</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Mode</label>
                  <select
                    value={interviewData.interviewMode}
                    onChange={(e) => handleChange('interviewMode', e.target.value)}
                    disabled={interviewData.interviewType === 'Assessment'}
                  >
                    <option value="Video Call">🎥 Video Call</option>
                    <option value="Phone Call">📞 Phone Call</option>
                    <option value="In-Person">🏢 In-Person</option>
                    {interviewData.interviewType === 'Assessment' && (
                      <option value="Online Test">📝 Online Test</option>
                    )}
                  </select>
                </div>
              </div>
            </div>

            {/* Video Call Configuration - Only for non-assessment */}
            {interviewData.interviewMode === 'Video Call' && interviewData.interviewType !== 'Assessment' && (
              <div className="form-section video-call-config">
                <div className="form-section-header">
                  <h4><FaVideo /> Video Call Configuration</h4>
                  <p className="form-section-subtitle">Choose meeting method</p>
                </div>
                
                <div className="meeting-options">
                  <label className="meeting-option">
                    <input
                      type="radio"
                      checked={interviewData.useJitsiMeet === true}
                      onChange={() => {
                        setInterviewData(prev => ({ ...prev, useJitsiMeet: true, meetingLink: '' }));
                      }}
                    />
                    <div className="meeting-option-content">
                      <strong>🎥 Built-in Jitsi Meet</strong>
                      <small>No external links needed - Direct video call in browser</small>
                    </div>
                  </label>
                  
                  <label className="meeting-option">
                    <input
                      type="radio"
                      checked={interviewData.useJitsiMeet === false}
                      onChange={() => setInterviewData(prev => ({ ...prev, useJitsiMeet: false }))}
                    />
                    <div className="meeting-option-content">
                      <strong>🔗 External Meeting Link</strong>
                      <small>Google Meet, Teams, Zoom, etc.</small>
                    </div>
                  </label>
                </div>

                {interviewData.useJitsiMeet ? (
                  <div className="jitsi-info-box">
                    <div className="info-icon">🎥</div>
                    <div className="info-content">
                      <strong>Jitsi Meet Integration Active</strong>
                      <p>Video call will start directly in this browser. No plugins or account needed.</p>
                      <button 
                        type="button" 
                        className="test-video-btn"
                        onClick={startVideoCall}
                      >
                        Test Video Call
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="form-group">
                    <label>Meeting Link *</label>
                    <input
                      type="url"
                      placeholder="https://meet.google.com/xxx-xxxx-xxx"
                      value={interviewData.meetingLink}
                      onChange={(e) => handleChange('meetingLink', e.target.value)}
                      className={errors.meetingLink ? 'input-error' : ''}
                    />
                    {errors.meetingLink && <div className="form-error">{errors.meetingLink}</div>}
                  </div>
                )}
              </div>
            )}

            {/* In-Person Location */}
            {interviewData.interviewMode === 'In-Person' && interviewData.interviewType !== 'Assessment' && (
              <div className="form-section">
                <div className="form-group">
                  <label><FaMapMarkerAlt /> Location *</label>
                  <input
                    type="text"
                    placeholder="Office address, floor, room number"
                    value={interviewData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    className={errors.location ? 'input-error' : ''}
                  />
                  {errors.location && <div className="form-error">{errors.location}</div>}
                </div>
              </div>
            )}

            {/* Question Selection for Assessment - ONLY for Assessment type */}
            {interviewData.interviewType === 'Assessment' && (
              <div className="form-section">
                <div className="form-section-header">
                  <div className="section-header-left">
                    <h4>Assessment Questions</h4>
                    <p className="form-section-subtitle">
                      Select questions from your question bank for <strong>{application.appliedRole}</strong> position
                    </p>
                  </div>
                  <div className="section-header-right">
                    <button 
                      type="button" 
                      className={`view-questions-btn ${showQuestionSelection ? 'active' : ''}`}
                      onClick={() => setShowQuestionSelection(!showQuestionSelection)}
                    >
                      {showQuestionSelection ? '▲ Hide Questions' : '▼ Select Questions'}
                    </button>
                    {interviewData.selectedQuestions.length > 0 && (
                      <button 
                        type="button"
                        className="generate-assessment-btn"
                        onClick={handleAutoGenerateAssessment}
                        disabled={generatingAssessment}
                      >
                        {generatingAssessment ? 'Generating...' : '⚡ Generate Assessment Link'}
                      </button>
                    )}
                  </div>
                </div>

                {errors.selectedQuestions && <div className="form-error">{errors.selectedQuestions}</div>}

                <div className="form-group">
                  <label>Selected Questions: {interviewData.selectedQuestions.length}</label>
                  {interviewData.selectedQuestions.length > 0 ? (
                    <div className="selected-questions-preview">
                      {interviewData.selectedQuestions.map(qId => {
                        const question = questions.find(q => q._id === qId);
                        return question ? (
                          <div key={qId} className="selected-question-item">
                            <span className="question-type-icon">{getQuestionTypeIcon(question.type)}</span>
                            <span className="question-text-truncated">
                              {question.question.length > 50 ? question.question.substring(0, 50) + '...' : question.question}
                            </span>
                            <button type="button" className="remove-question-btn" onClick={() => toggleQuestionSelection(qId)}>
                              ×
                            </button>
                          </div>
                        ) : null;
                      })}
                    </div>
                  ) : (
                    <div className="no-questions-selected">
                      <span className="no-questions-icon">📝</span>
                      No questions selected for this assessment
                    </div>
                  )}
                </div>

                {showQuestionSelection && (
                  <div className="questions-selection-modal">
                    <div className="questions-selection-header">
                      <h5>Select Questions for Assessment</h5>
                      <div className="questions-stats">
                        <span className="questions-count">{questions.length} questions available for {application.appliedRole}</span>
                        <span className="selected-count">{interviewData.selectedQuestions.length} selected</span>
                      </div>
                    </div>

                    {loadingQuestions ? (
                      <div className="questions-loading">
                        <div className="loading-spinner"></div>
                        Loading questions...
                      </div>
                    ) : questions.length === 0 ? (
                      <div className="no-questions-available">
                        <div className="no-questions-icon">📚</div>
                        <p>No questions available for <strong>{application.appliedRole}</strong> position</p>
                        <button type="button" className="create-questions-btn" onClick={() => window.open('/question-bank', '_blank')}>
                          Go to Question Bank
                        </button>
                      </div>
                    ) : (
                      <div className="questions-list">
                        {questions.map(question => (
                          <div key={question._id} className={`question-item ${interviewData.selectedQuestions.includes(question._id) ? 'question-selected' : ''}`}>
                            <div className="question-select-checkbox">
                              <input
                                type="checkbox"
                                checked={interviewData.selectedQuestions.includes(question._id)}
                                onChange={() => toggleQuestionSelection(question._id)}
                              />
                            </div>
                            <div className="question-content">
                              <div className="question-header">
                                <span className="question-type-badge">{getQuestionTypeIcon(question.type)} {question.type}</span>
                                <span className="question-difficulty-badge" style={{ backgroundColor: getDifficultyColor(question.difficulty) }}>
                                  {question.difficulty}
                                </span>
                                <span className="question-time-badge">⏱️ {question.timeLimit || 120}s</span>
                              </div>
                              <p className="question-text">{question.question}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Assessment Link Preview */}
                {assessmentCreated && interviewData.assessmentLink && (
                  <div className="assessment-link-preview">
                    <div className="link-preview-header">
                      <span className="link-icon">🔗</span>
                      <span>Assessment Link Generated</span>
                    </div>
                    <div className="link-preview-content">
                      <code>{interviewData.assessmentLink}</code>
                      <button 
                        type="button"
                        className="copy-link-btn"
                        onClick={() => {
                          navigator.clipboard.writeText(interviewData.assessmentLink);
                          toast.success('Assessment link copied!');
                        }}
                      >
                        Copy Link
                      </button>
                    </div>
                    <p className="link-note">
                      <strong>Note:</strong> This assessment will be taken online. No video call is required.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Interviewers Section */}
            <div className="form-section">
              <div className="form-group">
                <label><FaUsers /> {interviewData.interviewType === 'Assessment' ? 'Assessors' : 'Interviewers'} (comma separated)</label>
                <input
                  type="text"
                  placeholder="e.g., John Doe, Jane Smith"
                  value={interviewData.interviewers}
                  onChange={(e) => handleChange('interviewers', e.target.value)}
                />
              </div>
            </div>

            {/* Duration Section */}
            <div className="form-section">
              <div className="form-group">
                <label><FaClock /> Duration</label>
                <select value={interviewData.duration} onChange={(e) => handleChange('duration', e.target.value)}>
                  <option value="30">30 minutes</option>
                  <option value="45">45 minutes</option>
                  <option value="60">1 hour</option>
                  <option value="90">1 hour 30 minutes</option>
                  <option value="120">2 hours</option>
                  <option value="180">3 hours (For Assessment)</option>
                </select>
              </div>
            </div>

            {/* Notes Section */}
            <div className="form-section">
              <div className="form-group">
                <label><FaStickyNote /> Additional Notes</label>
                <textarea
                  placeholder={
                    interviewData.interviewType === 'Assessment' 
                      ? 'Assessment instructions, passing criteria, special notes...' 
                      : 'Topics to cover, preparation required, special instructions...'
                  }
                  value={interviewData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  rows="4"
                />
              </div>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <button type="button" className="cancel-btn" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="schedule-btn" disabled={generatingAssessment}>
                {generatingAssessment ? 'Generating...' : (interviewData.interviewType === 'Assessment' ? 'Schedule Assessment' : 'Schedule Interview')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default InterviewSchedule;